import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen } from 'lucide-react';

export default function Register({ setUser }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    role: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [loading, setLoading] = useState(false);

  const handleRoleSelect = (role) => {
    setFormData({ ...formData, role });
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    if (!formData.acceptTerms) {
      toast.error('Lütfen KVKK ve Aydınlatma metnini kabul edin');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/register', {
        role: formData.role,
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      
      const { access_token, user } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      toast.success('Kayıt başarılı! Hoş geldiniz.');
      navigate('/');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Kayıt başarısız. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-700 to-emerald-700 bg-clip-text text-transparent">Mizaç360</h1>
          </div>
        </div>

        <Card className="shadow-xl border-none" data-testid="register-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-slate-800">
              {step === 1 ? 'Rolünüzü Seçin' : 'Hesap Oluşturun'}
            </CardTitle>
            <CardDescription>
              {step === 1 ? 'Sisteme hangi rolde katılmak istersiniz?' : 'Kayıt bilgilerinizi girin'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <div className="space-y-3">
                {[
                  { value: 'student', label: 'Öğrenciyim', desc: 'Kendi mizaç profilimi keşfetmek istiyorum' },
                  { value: 'parent', label: 'Veliyim', desc: 'Çocuğumun mizaç profilini öğrenmek istiyorum' },
                  { value: 'teacher', label: 'Öğretmenim', desc: 'Öğrencilerimi daha iyi anlamak istiyorum' }
                ].map((role) => (
                  <Button
                    key={role.value}
                    variant="outline"
                    className="w-full h-auto p-4 flex flex-col items-start gap-1 hover:border-blue-600 hover:bg-blue-50 transition-colors"
                    onClick={() => handleRoleSelect(role.value)}
                    data-testid={`role-${role.value}-btn`}
                  >
                    <span className="font-semibold text-slate-800">{role.label}</span>
                    <span className="text-sm text-slate-600">{role.desc}</span>
                  </Button>
                ))}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Ad Soyad</Label>
                  <Input
                    id="name"
                    placeholder="Adınız ve Soyadınız"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="register-name-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-posta</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Şifre</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="register-password-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Şifre Tekrar</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    data-testid="register-confirm-password-input"
                    className="h-11"
                  />
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={formData.acceptTerms}
                    onCheckedChange={(checked) => setFormData({ ...formData, acceptTerms: checked })}
                    data-testid="register-terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                    KVKK ve Aydınlatma metnini okudum ve kabul ediyorum
                  </label>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setStep(1)}
                    data-testid="register-back-btn"
                  >
                    Geri
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? 'Oluşturuluyor...' : 'Hesap Oluştur'}
                  </Button>
                </div>
              </form>
            )}
            
            {step === 1 && (
              <div className="mt-6 text-center text-sm">
                <span className="text-slate-600">Zaten hesabınız var mı? </span>
                <Button
                  variant="link"
                  className="p-0 h-auto font-semibold text-blue-600 hover:text-blue-700"
                  onClick={() => navigate('/login')}
                  data-testid="register-to-login-link"
                >
                  Giriş Yap
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}